import React, { useState, useEffect } from 'react';
import Section from '../Div'; // your layout component
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

// -----------------------------------------------
// Mapping of sub-options to their descriptive text
// -----------------------------------------------
const descriptionsMapping = {
  '+EV': `The +EV model collects and displays player prop odds from DraftKings to take advantage of PrizePicks and Underdogs set payout system. The +EV Model shows you which player props are most favored by sports books so you're always betting on the favorite when the payout odds are set.`,

  'HotStreaks NBA': `The Hot Streaks Model collects and displays player props that are currently on streaks hitting the over/under consistently in their most recent games.`,

  'NBA Projections': `The NFL/NBA/MLB projections model is designed to predict the outcome of player props based on current matchup and recent games.`,

  'Opponent Points per Game': `Displays NBA team points, rebounds, and assist ALLOWED per game. These models highlight team defensive stats to ensure you're only betting on the best matchups each day.`,

  'Opponent Rebounds per Game': `Displays NBA team points, rebounds, and assist ALLOWED per game. These models highlight team defensive stats to ensure you're only betting on the best matchups each day.`,

  'Opponent Assists per Game': `Displays NBA team points, rebounds, and assist ALLOWED per game. These models highlight team defensive stats to ensure you're only betting on the best matchups each day.`,

  'Team Strikeouts per Game': `Displays each MLB team's average total strikeouts and hits per game. This helps you know which pitchers are facing the best and worst lineups each day.`,

  'Team Hits per Game': `Displays each MLB team's average total strikeouts and hits per game. This helps you know which pitchers are facing the best and worst lineups each day.`,

  'ColdHot Matchup': `Displays which hitters have a hot or cold matchup against that day's opposing pitcher. This model highlights which hitters are expected to have a good or bad game based on previous matchups against the same pitcher.`,

  'HotStreaks MLB': `The Hot Streaks Model collects and displays player props that are currently on streaks hitting the over/under consistently in their most recent games.`,

  '1st Inning Runs': `Displays each MLB team's average runs scored in the first inning. This model helps take advantage of the yes/no runs first inning betting prop, ensuring you're aware of which teams are best and worst in the first inning.`,

  'MLB Projections': `The NFL/NBA/MLB projections model is designed to predict the outcome of player props based on current matchup and recent games.`,

  'HotStreaks NFL': `The Hot Streaks Model collects and displays player props that are currently on streaks hitting the over/under consistently in their most recent games.`,

  'Rushing Yards Allowed': `Displays each NFL team's average pass yards and rush yards allowed per game. This ensures you know which players are facing the best and worst defenses each week.`,

  'Pass Yards Allowed': `Displays each NFL team's average pass yards and rush yards allowed per game. This ensures you know which players are facing the best and worst defenses each week.`,

  'NFL Projections': `The NFL/NBA/MLB projections model is designed to predict the outcome of player props based on current matchup and recent games.`
};

// -----------------------------------------------
// Sample data. In a real app, fetch from your API
// -----------------------------------------------
const sportsData = {
  NBA: {
    sidebarOptions: [
      'Opponent Points per Game',
      'Opponent Rebounds per Game',
      'Opponent Assists per Game',
      'HotStreaks NBA',
      'NBA Projections',
    ],
    tables: {}
  },
  MLB: {
    sidebarOptions: [
      'Team Strikeouts per Game',
      'Team Hits per Game',
      'ColdHot Matchup',
      'HotStreaks MLB',
      '1st Inning Runs',
      'MLB Projections',
    ],
    tables: {}
  },
  NFL: {
    sidebarOptions: [
      'HotStreaks NFL',
      'Rushing Yards Allowed',
      'Pass Yards Allowed',
      'NFL Projections',
    ],
    tables: {}
  },
  EV: {
    sidebarOptions: ['+EV'],
    tables: {}
  },
};

const nbaContent = [
  {
    title: 'Opponent Points per Game',
    description: 'Track how many points teams are allowing on average—key for spotting defensive weaknesses.',
    icon: '/images/chart-icon.png'
  },
  {
    title: 'Opponent Rebounds per Game',
    description: 'See which teams give up the most boards, revealing opportunities for rebound-heavy players.',
    icon: '/images/rebound-icon.png'
  },
  {
    title: 'Opponent Assists per Game',
    description: 'Find teams that allow the most ball movement—perfect for targeting playmakers.',
    icon: '/images/assist-icon.png'
  },
  {
    title: 'HotStreaks NBA',
    description: 'Stay updated with players currently on fire—ride the wave before it cools off.',
    icon: '/images/fire-icon.png'
  },
  {
    title: 'NBA Projections',
    description: 'Get smart, data-driven forecasts for player and team performance in upcoming games.',
    icon: '/images/projection-icon.png'
  }
];

const mlbContent = [
  {
    title: 'Team Strikeouts per Game',
    description: 'See how often teams strike out—essential for picking pitchers and avoiding cold bats.',
    icon: '/images/strikeout-icon.png'
  },
  {
    title: 'Team Hits per Game',
    description: 'Find teams that consistently make contact—great for targeting offensive momentum.',
    icon: '/images/hit-icon.png'
  },
  {
    title: 'ColdHot Matchup',
    description: 'Compare slumping teams vs hot performers—perfect for spotting exploitable matchups.',
    icon: '/images/hotcold-icon.png'
  },
  {
    title: 'HotStreaks MLB',
    description: 'Track MLB players on fire—capitalize on their momentum while it lasts.',
    icon: '/images/fire-icon.png'
  },
  {
    title: '1st Inning Runs',
    description: 'Analyze which teams start strong or slow—key for first-inning bets and predictions.',
    icon: '/images/inning-icon.png'
  },
  {
    title: 'MLB Projections',
    description: 'Get predictive insights on players and matchups—built for smarter daily decisions.',
    icon: '/images/projection-icon.png'
  }
];

const nflContent = [
  {
    title: 'HotStreaks NFL',
    description: 'Spot players heating up on the field—ride their momentum for weekly fantasy and bets.',
    icon: '/images/fire-icon.png'
  },
  {
    title: 'Rushing Yards Allowed',
    description: 'Track how many yards teams surrender on the ground—ideal for picking strong RB matchups.',
    icon: '/images/rush-icon.png'
  },
  {
    title: 'Pass Yards Allowed',
    description: 'See which defenses give up big air plays—great for targeting QBs and WRs.',
    icon: '/images/pass-icon.png'
  },
  {
    title: 'NFL Projections',
    description: 'Unlock weekly player and team forecasts—data-driven insights for smarter picks.',
    icon: '/images/projection-icon.png'
  }
];

const evContent = [
  {
    title: '+EV',
    description: 'Identify bets with a positive expected value—maximize profit by playing the long game smart.',
    icon: '/images/ev-icon.png'
  }
];

// -----------------------------------------------
// SVG Icons Components
// -----------------------------------------------
const PointsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="80" height="80" fill="#4A8BFF">
    <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/>
  </svg>
);

const ReboundsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="80" height="80" fill="#4A8BFF">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/>
    <path d="M13 7h-2v6h6v-2h-4z"/>
  </svg>
);

const AssistsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="80" height="80" fill="#4A8BFF">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
  </svg>
);

const HotStreaksIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="80" height="80" fill="#FF6B35">
    <path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67zM12 20c-3.31 0-6-2.69-6-6 0-1.53.3-3.04.86-4.43 1.01 1.01 2.41 1.63 3.97 1.63 2.66 0 4.75-1.83 5.28-4.43C17.34 8.97 18 11.44 18 14c0 3.31-2.69 6-6 6z"/>
  </svg>
);

const ProjectionsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="80" height="80" fill="#4A8BFF">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-5h2v5zm4 0h-2v-7h2v7zm4 0h-2V7h2v10z"/>
  </svg>
);

const StrikeoutIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="80" height="80" fill="#4A8BFF">
    <path d="M12.5 8.5l1.5 1.5h5v-2h-5l-1.5 1.5zM10 6L9 5 5 9l4 4 1-1-3-3 3-3zm4 12l1 1 4-4-4-4-1 1 3 3-3 3z"/>
    <path d="M12 18c-3.31 0-6-2.69-6-6 0-1.93.91-3.65 2.33-4.75l7.66 7.66C14.65 17.09 12.93 18 12 18zm0-12c3.31 0 6 2.69 6 6 0 1.93-.91 3.65-2.34 4.75L8 9.09C9.35 6.91 11.07 6 12 6z"/>
  </svg>
);

const HitsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="80" height="80" fill="#4A8BFF">
    <path d="M18 13h-.68l-2 2h1.91L19 17H5l1.78-2h2.05l-2-2H6l-3 3v4c0 1.1.89 2 1.99 2H19c1.1 0 2-.89 2-2v-4l-3-3zm-1-5.05l-4.95 4.95-3.54-3.54 4.95-4.95L17 7.95zm-6.83 1.42L12 11.19l4.95-4.95L14.15 3.5l-4.95 4.95 1.42 1.42z"/>
  </svg>
);

const HotColdIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="80" height="80" fill="#4A8BFF">
    <path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67zM12 20c-3.31 0-6-2.69-6-6 0-1.53.3-3.04.86-4.43 1.01 1.01 2.41 1.63 3.97 1.63 2.66 0 4.75-1.83 5.28-4.43C17.34 8.97 18 11.44 18 14c0 3.31-2.69 6-6 6z"/>
  </svg>
);

const InningIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="80" height="80" fill="#4A8BFF">
    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
  </svg>
);

const RushingIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="80" height="80" fill="#4A8BFF">
    <path d="M13.49 5.48c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm-3.6 13.9l1-4.4 2.1 2v6h2v-7.5l-2.1-2 .6-3c1.3 1.5 3.3 2.5 5.5 2.5v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1l-5.2 2.2v4.7h2v-3.4l1.8-.7-1.6 8.1-4.9-1-.4 2 7 1.4z"/>
  </svg>
);

const PassingIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="80" height="80" fill="#4A8BFF">
    <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-6 5h-2V5h2v2zm2 0h2V5h-2v2zm-4 8l-4-4 1.41-1.41L12 13.17l4.59-4.58L18 10l-6 6z"/>
  </svg>
);

const EVIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="80" height="80" fill="#FF6B35">
    <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
  </svg>
);

export default function SportsSidebar() {
  const [activeTab, setActiveTab] = useState('NBA');
  const [isMobile, setIsMobile] = useState(false);

  // Check if screen is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 480);
    };
    
    // Check on initial load
    checkMobile();
    
    // Check on resize
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
  };

  // Number indicator component
  const NumberIndicator = ({ number }) => (
    <div 
      style={{ 
        width: isMobile ? '30px' : '40px', 
        height: isMobile ? '30px' : '40px', 
        borderRadius: '50%', 
        backgroundColor: '#222222', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: isMobile ? '12px' : '14px',
        fontWeight: 'bold',
        color: '#ffffff'
      }}
    >
      {number < 10 ? `0${number}` : number}
    </div>
  );

  // Function to get the appropriate icon based on the title
  const getIconComponent = (title) => {
    // Use the same icon components but resize them for mobile
    const IconComponent = (() => {
      if (title.includes('Points')) return PointsIcon;
      if (title.includes('Rebounds')) return ReboundsIcon;
      if (title.includes('Assists')) return AssistsIcon;
      if (title.includes('HotStreaks') || title.includes('Hot Streaks')) return HotStreaksIcon;
      if (title.includes('Projections')) return ProjectionsIcon;
      if (title.includes('Strikeouts')) return StrikeoutIcon;
      if (title.includes('Hits')) return HitsIcon;
      if (title.includes('ColdHot') || title.includes('Cold Hot')) return HotColdIcon;
      if (title.includes('Inning')) return InningIcon;
      if (title.includes('Rushing')) return RushingIcon;
      if (title.includes('Pass')) return PassingIcon;
      if (title.includes('EV')) return EVIcon;
      return ProjectionsIcon; // Default
    })();
    
    // Return the component with adjusted size for mobile
    return <IconComponent size={isMobile ? 50 : 80} />;
  };

  const renderContent = () => {
    let contentToRender = [];
    
    if (activeTab === 'NBA') {
      contentToRender = nbaContent;
    } else if (activeTab === 'MLB') {
      contentToRender = mlbContent;
    } else if (activeTab === 'NFL') {
      contentToRender = nflContent;
    } else if (activeTab === 'EV') {
      contentToRender = evContent;
    } else {
      return (
        <>
          {/* Feature 1 */}
          <div className="feature-item" style={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            marginBottom: '30px',
            flexDirection: isMobile ? 'column' : 'row' 
          }}>
            <img 
              src="/images/crown.png" 
              alt="Crown Icon" 
              style={{ 
                marginRight: isMobile ? '0' : '15px', 
                marginBottom: isMobile ? '10px' : '0',
                width: isMobile ? '30px' : '40px', 
                height: isMobile ? '30px' : '40px' 
              }} 
            />
            <div>
              <h3 style={{ 
                fontSize: isMobile ? '18px' : '24px', 
                marginBottom: '10px', 
                color: '#ffffff' 
              }}>
                Thousands of, Sorted by Probability
              </h3>
              <p style={{ 
                color: '#cccccc', 
                fontSize: isMobile ? '14px' : '16px' 
              }}>
                Quickly find the props that are most likely to win across multiple sports and a variety of prop sites.
              </p>
            </div>
          </div>

          {/* Feature 2 */}
          <div className="feature-item" style={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            marginBottom: '30px',
            flexDirection: isMobile ? 'column' : 'row' 
          }}>
            <img 
              src="/images/crown.png" 
              alt="Crown Icon" 
              style={{ 
                marginRight: isMobile ? '0' : '15px', 
                marginBottom: isMobile ? '10px' : '0',
                width: isMobile ? '30px' : '40px', 
                height: isMobile ? '30px' : '40px' 
              }} 
            />
            <div>
              <h3 style={{ 
                fontSize: isMobile ? '18px' : '24px', 
                marginBottom: '10px', 
                color: '#ffffff' 
              }}>
                Expected Value Calculator
              </h3>
              <p style={{ 
                color: '#cccccc', 
                fontSize: isMobile ? '14px' : '16px' 
              }}>
                Choose your favorite props and calculate how the size of your advantage.
              </p>
            </div>
          </div>

          {/* Feature 3 */}
          <div className="feature-item" style={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            marginBottom: '30px',
            flexDirection: isMobile ? 'column' : 'row' 
          }}>
            <img 
              src="/images/crown.png" 
              alt="Crown Icon" 
              style={{ 
                marginRight: isMobile ? '0' : '15px', 
                marginBottom: isMobile ? '10px' : '0',
                width: isMobile ? '30px' : '40px', 
                height: isMobile ? '30px' : '40px' 
              }} 
            />
            <div>
              <h3 style={{ 
                fontSize: isMobile ? '18px' : '24px', 
                marginBottom: '10px', 
                color: '#ffffff' 
              }}>
                Find Favorable Lines
              </h3>
              <p style={{ 
                color: '#cccccc', 
                fontSize: isMobile ? '14px' : '16px' 
              }}>
                Discover the lines that are too short or too long, giving you an extra advantage when it's time to wager.
              </p>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        {contentToRender.map((item, index) => {
          // For mobile, always use a vertical layout
          const layout = isMobile ? 'column' : (index % 2 === 0 ? 'row' : 'row-reverse');
          
          return (
            <div key={index} className="how-it-works-item" style={{ 
              display: 'flex', 
              flexDirection: layout,
              alignItems: isMobile ? 'center' : 'center', 
              marginBottom: isMobile ? '40px' : '60px',
              position: 'relative'
            }}>
              {/* On mobile, we'll put the number on top */}
              {isMobile && (
                <div style={{ 
                  position: 'relative',
                  width: '30px', 
                  height: '30px', 
                  marginBottom: '15px'
                }}>
                  <NumberIndicator number={index + 1} />
                </div>
              )}
              
              {/* Icon section */}
              <div style={{ 
                flex: isMobile ? 'none' : '1', 
                display: 'flex', 
                justifyContent: isMobile ? 'center' : (index % 2 === 0 ? 'flex-end' : 'flex-start'),
                padding: isMobile ? '0 0 15px 0' : (index % 2 === 0 ? '0 20px 0 0' : '0 0 0 20px')
              }}>
                <div style={{ 
                  width: isMobile ? '50px' : '80px', 
                  height: isMobile ? '50px' : '80px', 
                  borderRadius: '5px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#4A8BFF'
                }}>
                  {getIconComponent(item.title)}
                </div>
              </div>
              
              {/* Center divider with number - only on desktop */}
              {!isMobile && (
                <div style={{ 
                  position: 'relative',
                  width: '1px', 
                  height: '100%', 
                  backgroundColor: '#444444',
                  margin: '0 40px'
                }}>
                  <NumberIndicator number={index + 1} />
                </div>
              )}
              
              {/* Text section */}
              <div style={{ 
                flex: isMobile ? 'none' : '1', 
                textAlign: isMobile ? 'center' : (index % 2 === 0 ? 'left' : 'right'),
                width: isMobile ? '100%' : 'auto',
                padding: isMobile ? '0' : (index % 2 === 0 ? '0 0 0 20px' : '0 20px 0 0')
              }}>
                <h3 style={{ 
                  fontSize: isMobile ? '16px' : '18px', 
                  marginBottom: isMobile ? '5px' : '10px', 
                  color: '#ffffff' 
                }}>
                  {item.title}
                </h3>
                <p style={{ 
                  color: '#cccccc', 
                  fontSize: isMobile ? '12px' : '14px', 
                  lineHeight: '1.5' 
                }}>
                  {/* Truncate the description on mobile if it's too long */}
                  {isMobile && item.description.length > 80 
                    ? `${item.description.substring(0, 80)}...` 
                    : item.description}
                </p>
              </div>
            </div>
          );
        })}
      </>
    );
  };

  return (
    <Section
      className="position-relative"
      style={{ 
        minHeight: '50vh', 
        padding: isMobile ? '1rem' : '2rem',
        backgroundColor: '#121212',
        color: '#ffffff'
      }}
    >
      {/* ------------------ TOP TOGGLES ------------------ */}
      <Section style={{ width: '100%', textAlign: 'center', marginBottom: '1.5rem' }}>
        <ul
          className="cs-tab_links cs-style1 cs-primary_font"
          style={{ 
            position: 'relative', 
            top: '0', 
            display: 'inline-block',
            flexWrap: isMobile ? 'wrap' : 'nowrap'
          }}
        >
          {['NBA', 'MLB', 'NFL', 'EV'].map((sport, index) => (
            <li
              key={index}
              className={activeTab === sport ? 'active mr-3' : 'mr-3'}
              onClick={() => handleTabChange(sport)}
              style={{ 
                cursor: 'pointer', 
                marginRight: isMobile ? '0.8rem' : '1.5rem',
                marginBottom: isMobile ? '5px' : '0',
                fontSize: isMobile ? '0.9rem' : '1.1rem',
                color: activeTab === sport ? '#ffffff' : '#aaaaaa',
                fontWeight: activeTab === sport ? 'bold' : 'normal'
              }}
            >
              {sport === 'EV' ? '+EV' : sport}
            </li>
          ))}
        </ul>
      </Section>

      {/* ------------------ CONTENT ------------------ */}
      <Section className="how-it-works-content" style={{ 
        maxWidth: '900px', 
        margin: '0 auto',
        padding: isMobile ? '0 10px' : '0'
      }}>
        {renderContent()}
      </Section>
    </Section>
  );
}
